// AI Service for Style AI - Safe GPT-4 Integration
// Uses backend proxy approach to avoid CORS and security issues

import { getAuthHeaders } from './usageService'
import { PLANS } from '../data/plansConfig'

// Configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3003/api'
const USE_AI_API = import.meta.env.VITE_USE_AI_API !== 'false'

// Fallback responses for when API is not available
const fallbackResponses = {
  design: [
    `Let's design it! Open **Custom** and pick the garment, material, color, and style. Every feature is free; your plan only sets how many AI generations you get each week (${PLANS.free.weekly.pod} graphic + ${PLANS.free.weekly.custom} Custom on Free).`,
    "Happy to help you create a custom design. Tell me the garment type, material, color, and vibe, then generate it in **Custom** or place your own artwork in **Design Studio**.",
    "Great idea! Start in **Custom** for a full custom garment, or **Design Studio** to put your graphic on a printed blank. What are you making?"
  ],
  material: [
    "I can help you choose the perfect materials! Here are some popular options:\n\n• **Cotton**: Breathable, comfortable, great for everyday wear\n• **Wool**: Warm, durable, perfect for winter clothing\n• **Leather**: Rich, durable, great for jackets and accessories\n• **Silk**: Luxurious, lightweight, perfect for formal wear\n• **Denim**: Durable, versatile, classic for jeans and jackets\n\nWhat type of clothing are you designing? I can recommend the best materials for your project.",
    "Great question about materials! Here's what I recommend:\n\n🌿 **Natural Fibers**: Cotton, wool, silk, linen\n🛡️ **Durable Options**: Leather, denim, canvas\n✨ **Luxury Materials**: Cashmere, silk, fine leather\n\nWhat's your project? I can suggest the perfect material combination!",
    "Materials are crucial for the perfect design! Consider:\n\n• **Comfort**: Cotton, silk, cashmere\n• **Durability**: Leather, denim, canvas\n• **Style**: Wool, velvet, suede\n• **Function**: Performance fabrics, technical materials\n\nWhat are you creating? I'll recommend the ideal materials!"
  ],
  premium: [
    `Every Style AI feature is free, including Custom and Design Studio. **Pro** ($${PLANS.pro.priceMonthlyUsd}/mo) gives you more generations every week: ${PLANS.pro.weekly.pod} graphic + ${PLANS.pro.weekly.custom} Custom, versus ${PLANS.free.weekly.pod} + ${PLANS.free.weekly.custom} on Free.`,
    `Free includes every feature with ${PLANS.free.weekly.pod} graphic + ${PLANS.free.weekly.custom} Custom generations per week. **Pro** is $${PLANS.pro.priceMonthlyUsd}/mo for ${PLANS.pro.weekly.pod} graphic + ${PLANS.pro.weekly.custom} Custom per week. Canvas edits never count against your limit.`
  ],
  default: [
    "I'm your Style AI assistant, here to help with all things fashion and design! I can help you:\n\n🎨 **Create custom designs**\n🔍 **Search for existing clothing**\n💡 **Get style advice**\n📚 **Learn about materials and trends**\n\nWhat would you like to explore today?",
    "Welcome to Style AI! I'm here to help you with:\n\n✨ **Custom Design Creation**: Make your vision reality\n🔍 **Product Discovery**: Find the perfect pieces\n💡 **Style Guidance**: Get fashion advice\n📚 **Material Knowledge**: Learn about fabrics\n\nWhat can I help you with today?",
    "Hi there! I'm your Style AI assistant. I can help you:\n\n🎨 **Design Custom Clothing**: Create unique pieces\n🔍 **Find Existing Items**: Search our product database\n💡 **Get Style Advice**: Fashion recommendations\n📚 **Learn About Materials**: Fabric education\n\nWhat would you like to explore?"
  ]
}

function enrichActions(userInput, actions = [], isPremium = false, designPrefill, searchPrefill) {
  const next = [...actions]
  const input = userInput.toLowerCase()
  const has = (path) => next.some((a) => a.path === path)

  if ((input.includes('sketch') || input.includes('draw')) && !has('/design')) {
    next.push({
      type: 'navigate',
      path: '/design',
      label: 'Open Custom',
      hint: 'Describe your design with specs'
    })
  }
  if ((input.includes('search') || input.includes('find') || input.includes('buy')) && !has('/search')) {
    next.push({ type: 'navigate', path: '/search', label: 'Search products', hint: 'Find real items to buy' })
  }
  if ((input.includes('design') || input.includes('create') || input.includes('hoodie') || input.includes('dress')) && designPrefill && !has('/design')) {
    next.push({ type: 'navigate', path: '/design', label: `Design this ${designPrefill.type || 'garment'}`, hint: 'Opens Custom with specs' })
  }
  if (searchPrefill && !has('/search')) {
    next.push({ type: 'navigate', path: '/search', label: 'Search products', hint: searchPrefill.query || '' })
  }
  return next
}

// Enhanced AI response generation with safe API calls
export const generateAIResponse = async (userInput, conversationHistory = [], optionsOrPremium = false) => {
  const options = typeof optionsOrPremium === 'boolean'
    ? { isPremium: optionsOrPremium }
    : (optionsOrPremium || {})

  const {
    isPremium = false,
    isSignedIn = false,
    currentPath = '/',
    userName = ''
  } = options

  try {
    if (!USE_AI_API) {
      return {
        text: generateFallbackResponse(userInput, isPremium),
        actions: [],
        model: 'fallback'
      }
    }

    const headers = await getAuthHeaders()
    const payload = {
      message: userInput,
      conversationHistory: conversationHistory.slice(-20),
      context: {
        platform: 'Style AI',
        isSignedIn,
        currentPath,
        userName: userName ? userName.split('@')[0] : ''
      },
      aiCapabilities: ['design', 'search', 'explore', 'materials', 'style-advice']
    }

    const response = await fetch(`${API_BASE_URL}/ai/chat`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload)
    })

    if (response.status === 401) {
      const err = new Error('Please sign in to use the assistant.')
      err.code = 'AUTH_REQUIRED'
      throw err
    }

    if (response.status === 403) {
      const data = await response.json().catch(() => ({}))
      return {
        text: data.message || 'Please verify your email to use the assistant.',
        actions: [],
        model: 'account'
      }
    }

    if (response.status === 429) {
      return {
        text: 'You’re sending messages too quickly. Please wait a moment and try again.',
        actions: [],
        model: 'rate-limit'
      }
    }

    if (response.status === 400) {
      try {
        const data = await response.json()
        if (data.error === 'MESSAGE_TOO_LONG') {
          return {
            text: data.message || 'That message is too long. Please shorten it and try again.',
            actions: [],
            model: 'validation'
          }
        }
      } catch (_) {}
    }

    if (response.status === 422) {
      try {
        const data = await response.json()
        return {
          text: data.message || "We can't send that message. Please edit it and try again.",
          actions: [],
          model: 'moderation'
        }
      } catch (_) {}
      return {
        text: "We can't send that message. Please edit it and try again.",
        actions: [],
        model: 'moderation'
      }
    }

    if (!response.ok) {
      console.error('Assistant request failed', response.status)
      throw new Error('The assistant is unavailable right now. Please try again.')
    }

    const data = await response.json()
    const designPrefill = data.designPrefill || null
    const searchPrefill = data.searchPrefill || null
    return {
      text: data.response || generateFallbackResponse(userInput, isPremium),
      actions: enrichActions(userInput, data.actions || [], isPremium, designPrefill, searchPrefill),
      designPrefill,
      searchPrefill,
      model: data.model || 'assistant'
    }
  } catch (error) {
    if (error.code === 'AUTH_REQUIRED') throw error
    console.error('AI API Error:', error)
    const designPrefill = null
    return {
      text: generateFallbackResponse(userInput, isPremium),
      actions: enrichActions(userInput, inferLocalActions(userInput, isPremium), isPremium, designPrefill, null),
      model: 'fallback'
    }
  }
}

function inferLocalActions(userInput, isPremium) {
  const input = userInput.toLowerCase()
  const actions = []
  if (input.includes('sketch') || input.includes('draw')) {
    actions.push({ type: 'navigate', path: '/design', label: 'Open Custom' })
  } else if (input.includes('search') || input.includes('find') || input.includes('buy')) {
    actions.push({ type: 'navigate', path: '/search', label: 'Search clothing' })
  } else if (input.includes('design') || input.includes('create') || input.includes('generate')) {
    actions.push({ type: 'navigate', path: '/design', label: 'Open Custom' })
  }
  return actions
}

// Enhanced fallback response system
const generateFallbackResponse = (userInput, isPremium) => {
  const input = userInput.toLowerCase()
  
  // Design-related questions
  if (input.includes('design') || input.includes('create') || input.includes('custom')) {
    if (!isPremium) {
      return fallbackResponses.design[Math.floor(Math.random() * fallbackResponses.design.length)]
    }
    return "Perfect! I can help you create a custom design. Let's start with the basics:\n1. What type of clothing?\n2. Preferred material?\n3. Quality level?\n4. Any specific style preferences?\n\nOnce you provide these details, I'll generate a unique design for you!"
  }
  
  // Material questions
  if (input.includes('material') || input.includes('fabric') || input.includes('cotton') || input.includes('wool') || input.includes('leather') || input.includes('silk')) {
    return fallbackResponses.material[Math.floor(Math.random() * fallbackResponses.material.length)]
  }
  
  // Plan questions
  if (input.includes('pro plan') || input.includes('premium') || input.includes('upgrade') || input.includes('subscription') || input.includes('price') || input.includes('cost')) {
    return fallbackResponses.premium[Math.floor(Math.random() * fallbackResponses.premium.length)]
  }
  
  // Search questions
  if (input.includes('search') || input.includes('find') || input.includes('buy') || input.includes('shop') || input.includes('where')) {
    return "I can help you find the perfect clothing! Use our search feature to filter by:\n\n• **Type**: Jackets, dresses, shirts, pants, etc.\n• **Material**: Cotton, wool, leather, silk, etc.\n• **Quality**: Budget-friendly to luxury\n• **Price Range**: Set your budget\n• **Brand**: Find specific designers\n\nI'll show you real products from actual brands with direct purchase links. What are you looking for?"
  }
  
  // Style and fashion advice
  if (input.includes('style') || input.includes('fashion') || input.includes('trend') || input.includes('outfit') || input.includes('look')) {
    return "I love helping with style advice! Here are some current trends:\n\n• **Sustainable Fashion**: Eco-friendly materials and ethical production\n• **Minimalist Design**: Clean lines and simple aesthetics\n• **Vintage Revival**: Classic styles with modern twists\n• **Tech-Integrated**: Smart fabrics and functional design\n• **Personal Expression**: Custom designs that reflect your personality\n\nWhat's your style preference? I can help you find or create the perfect piece!"
  }
  
  // Specific clothing types
  if (input.includes('jacket') || input.includes('coat') || input.includes('blazer')) {
    return "Great choice! Jackets are versatile pieces. What material are you thinking? We have options like leather, denim, wool, or synthetic materials. Also, what's your preferred quality level - budget-friendly, mid-range, or high-end?"
  }
  
  if (input.includes('dress') || input.includes('gown') || input.includes('skirt')) {
    return "Dresses are perfect for any occasion! What style are you looking for?\n\n👗 **Casual**: Everyday comfort and style\n👗 **Formal**: Elegant and sophisticated\n👗 **Vintage**: Classic with modern twists\n👗 **Contemporary**: Trendy and fresh\n\nWhat's the occasion? I can help you find or design the perfect dress!"
  }
  
  // Technical questions
  if (input.includes('how') || input.includes('what') || input.includes('where') || input.includes('when') || input.includes('why')) {
    return "I'm here to help with all your fashion and design questions! I can assist with:\n\n• **Design Creation**: Help you design custom clothing\n• **Material Selection**: Choose the best fabrics for your project\n• **Style Advice**: Get fashion recommendations\n• **Product Search**: Find existing clothing items\n• **Pro plan**: More generations every week\n\nWhat specific question do you have? I'm ready to help!"
  }
  
  // Default response
  return fallbackResponses.default[Math.floor(Math.random() * fallbackResponses.default.length)]
}

// AI-powered design suggestions
export const generateDesignSuggestions = async (userPreferences) => {
  try {
    if (!USE_AI_API) {
      return generateFallbackDesignSuggestions(userPreferences)
    }

    const payload = {
      preferences: userPreferences,
      type: 'design_suggestions'
    }

    const response = await fetch(`${API_BASE_URL}/ai/design-suggestions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload)
    })

    if (!response.ok) {
      console.error('Assistant request failed', response.status)
      throw new Error('The assistant is unavailable right now. Please try again.')
    }

    const data = await response.json()
    return data.suggestions || generateFallbackDesignSuggestions(userPreferences)

  } catch (error) {
    console.error('Design Suggestions Error:', error)
    return generateFallbackDesignSuggestions(userPreferences)
  }
}

const generateFallbackDesignSuggestions = (preferences) => {
  const { type, material, quality } = preferences
  
  return `Based on your preferences for a ${quality} ${material} ${type}, here are 3 unique design suggestions:

**1. The Urban Classic**
- A modern take on timeless ${type} design
- Features clean lines and functional details
- Perfect for everyday wear with a sophisticated edge
- Best for: Professional settings and casual outings

**2. The Sustainable Statement**
- Eco-conscious ${material} construction
- Innovative design elements that reduce waste
- Comfortable and stylish with environmental responsibility
- Best for: Conscious consumers who value both style and ethics

**3. The Tech-Forward**
- Smart fabric integration and performance features
- Contemporary design with cutting-edge materials
- Versatile styling options for multiple occasions
- Best for: Active lifestyles and modern fashion enthusiasts

Each design can be customized further based on your specific needs and preferences!`
}

// AI-powered style analysis
export const analyzeUserStyle = async (userInput) => {
  try {
    if (!USE_AI_API) {
      return "I'd love to analyze your style preferences! Tell me more about your favorite colors, patterns, and clothing types. I can help you discover your unique style profile and suggest designs that match your personality."
    }

    const payload = {
      userInput: userInput,
      type: 'style_analysis'
    }

    const response = await fetch(`${API_BASE_URL}/ai/style-analysis`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload)
    })

    if (!response.ok) {
      console.error('Assistant request failed', response.status)
      throw new Error('The assistant is unavailable right now. Please try again.')
    }

    const data = await response.json()
    return data.analysis || "I'd love to analyze your style preferences! Tell me more about your favorite colors, patterns, and clothing types. I can help you discover your unique style profile and suggest designs that match your personality."

  } catch (error) {
    console.error('Style Analysis Error:', error)
    return "I'd love to analyze your style preferences! Tell me more about your favorite colors, patterns, and clothing types. I can help you discover your unique style profile and suggest designs that match your personality."
  }
}

// Health check for AI service
export const checkAIServiceHealth = async () => {
  try {
    if (!USE_AI_API) {
      return { status: 'fallback', message: 'Using fallback responses' }
    }

    const response = await fetch(`${API_BASE_URL}/ai/health`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    })

    if (response.ok) {
      const data = await response.json()
      return { status: 'connected', message: data.message }
    } else {
      return { status: 'error', message: 'AI service unavailable' }
    }

  } catch (error) {
    return { status: 'fallback', message: 'Using fallback responses' }
  }
} 